import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { useCallback, useEffect, useRef, useState } from 'react';
import { s } from '../../theme/tokens';
import { uiTokens } from '../../theme/uiTokens';
import { formatDuration } from '../formatters/formatDuration';
import { Box } from './Box';
import { formatChartDateLabel } from './chartDateLabel';
import { getChartTooltipSx } from './chartTooltipStyles';
import { useTheme } from './hooks';
import { Text } from './Text';
import { MuiLineChart } from './xCharts';
import type { ChartsAxisData } from '@mui/x-charts/models';
import type { LineItemIdentifier } from '@mui/x-charts/models/seriesType';
import type { MouseEvent as ReactMouseEvent } from 'react';

export type LineChartDatum = Record<string, string | number>;

type LineSeries = { key: string; label: string; color?: string };

const metricNumberFormatter = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 2,
});

type LineChartProps = {
  data: LineChartDatum[];
  series: LineSeries[];
  height?: number;
  marginLeft?: number;
  marginRight?: number;
  marginTop?: number;
  marginBottom?: number;
  yAxisWidth?: number;
  axisStrokeWidth?: number;
  showLegend?: boolean;
  showHorizontalGrid?: boolean;
  hideXAxisLine?: boolean;
  hideXAxisLabels?: boolean;
  hideYAxisLabels?: boolean;
  hideXAxisTicks?: boolean;
  hideYAxisTicks?: boolean;
  onSelectBar?: (dataIndex: number) => void;
  showPoints?: boolean;
  disableInteraction?: boolean;
  disableTooltip?: boolean;
  yDomainPaddingRatio?: number;
  yDomainPaddingTopRatio?: number;
  yDomainPaddingBottomRatio?: number;
  yReferenceLines?: Array<{ value: number; color: string; label?: string }>;
};

const buildTickLabelInterval = (
  totalPoints: number,
): ((value: string, index: number) => boolean) | 'auto' => {
  const targetTickCount = 8;
  if (totalPoints <= targetTickCount) return 'auto';
  const step = Math.ceil((totalPoints - 1) / (targetTickCount - 1));
  return (_value: string, index: number) =>
    index === 0 || index === totalPoints - 1 || index % step === 0;
};

export const LineChart = ({
  data,
  series,
  height = 260,
  marginLeft = -8,
  marginRight = 16,
  marginTop = 16,
  marginBottom = 14,
  yAxisWidth = 72,
  axisStrokeWidth = 1,
  showLegend = true,
  showHorizontalGrid = true,
  hideXAxisLine = true,
  hideXAxisLabels = false,
  hideYAxisLabels = false,
  hideXAxisTicks = false,
  hideYAxisTicks = false,
  onSelectBar,
  showPoints = false,
  disableInteraction = false,
  disableTooltip = false,
  yDomainPaddingRatio = 0,
  yDomainPaddingTopRatio,
  yDomainPaddingBottomRatio,
  yReferenceLines = [],
}: LineChartProps): JSX.Element => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [plotBounds, setPlotBounds] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const hasLegendLabels = series.some((item) => item.label.trim().length > 0);
  const hideLegend = !showLegend || !hasLegendLabels;
  const palette = [
    uiTokens.statusColors.purple,
    uiTokens.statusColors.green,
    uiTokens.statusColors.orange,
    uiTokens.statusColors.blue,
    uiTokens.statusColors.red,
  ];
  const xLabels = data.map((item) => formatChartDateLabel(String(item.label)));
  const xTickLabelInterval = buildTickLabelInterval(xLabels.length);
  const xAxisHeight = hideXAxisLabels && hideXAxisTicks ? 16 : 24;
  const lastClickRef = useRef<{ dataIndex: number; timestamp: number } | null>(null);
  const lastAxisClickRef = useRef<{ dataIndex: number; timestamp: number } | null>(null);
  const handleItemClick = useCallback(
    (_event: ReactMouseEvent<SVGElement>, item: LineItemIdentifier) => {
      if (!onSelectBar) return;
      const dataIndex = item.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      const now = Date.now();
      const lastClick = lastClickRef.current;
      if (lastClick?.dataIndex === dataIndex && now - lastClick.timestamp < 400) {
        return;
      }
      lastClickRef.current = { dataIndex, timestamp: now };
      onSelectBar(dataIndex);
    },
    [onSelectBar],
  );
  const handleAxisClick = useCallback(
    (_event: MouseEvent, axisData: ChartsAxisData | null) => {
      if (!onSelectBar) return;
      const dataIndex = axisData?.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      const now = Date.now();
      const lastAxisClick = lastAxisClickRef.current;
      if (lastAxisClick?.dataIndex === dataIndex && now - lastAxisClick.timestamp < 400) {
        return;
      }
      lastAxisClickRef.current = { dataIndex, timestamp: now };
      onSelectBar(dataIndex);
    },
    [onSelectBar],
  );
  const formatTooltipValue = (seriesKey: string, value: number | null): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '';
    const normalizedSeriesKey = seriesKey.toLowerCase();
    if (normalizedSeriesKey === 'duration') {
      return formatDuration(value * 1000);
    }
    if (normalizedSeriesKey === 'passrate') {
      return `${metricNumberFormatter.format(value)}%`;
    }
    return metricNumberFormatter.format(value);
  };
  const isDurationChart = series.some((item) => item.key.toLowerCase() === 'duration');
  const isPassRateChart = series.some((item) => item.key.toLowerCase() === 'passrate');
  const seriesMin = series.reduce((minValue, item) => {
    const itemMin = data.reduce((rowMin, row) => {
      const value = Number(row[item.key]);
      return Number.isFinite(value) && value < rowMin ? value : rowMin;
    }, Number.POSITIVE_INFINITY);
    return itemMin < minValue ? itemMin : minValue;
  }, Number.POSITIVE_INFINITY);
  const seriesMax = series.reduce((maxValue, item) => {
    const itemMax = data.reduce((rowMax, row) => {
      const value = Number(row[item.key]);
      return Number.isFinite(value) && value > rowMax ? value : rowMax;
    }, 0);
    return itemMax > maxValue ? itemMax : maxValue;
  }, 0);
  const formatYAxisValue = (value: number | null): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '';
    if (!isDurationChart) return String(value);
    return formatDuration(value * 1000);
  };
  const finiteReferenceLines = yReferenceLines.filter((line) => Number.isFinite(line.value));
  const referenceMax = finiteReferenceLines.reduce(
    (maxValue, line) => (line.value > maxValue ? line.value : maxValue),
    0,
  );
  const rawYAxisMin = Number.isFinite(seriesMin) ? Math.min(0, seriesMin) : 0;
  const rawYAxisMax = isPassRateChart ? 100 : Math.max(seriesMax, referenceMax);
  const yAxisRange = rawYAxisMax - rawYAxisMin;
  const yAxisPaddingBase =
    yAxisRange > 0 ? yAxisRange : rawYAxisMax !== 0 ? Math.abs(rawYAxisMax) : 1;
  const resolvedTopPaddingRatio = yDomainPaddingTopRatio ?? yDomainPaddingRatio;
  const resolvedBottomPaddingRatio = yDomainPaddingBottomRatio ?? yDomainPaddingRatio;
  const yAxisTopPadding =
    resolvedTopPaddingRatio > 0 ? yAxisPaddingBase * resolvedTopPaddingRatio : 0;
  const yAxisBottomPadding =
    resolvedBottomPaddingRatio > 0 ? yAxisPaddingBase * resolvedBottomPaddingRatio : 0;
  const yAxisMin = rawYAxisMin - yAxisBottomPadding;
  const yAxisMax = rawYAxisMax + yAxisTopPadding;
  const yAxisTickTranslateX = isDurationChart ? -8 : -6;
  const tooltipTrigger = disableTooltip ? 'none' : undefined;

  const resolveSeriesLabel = (item: LineSeries): string => {
    const trimmed = item.label.trim();
    if (trimmed.length > 0) return trimmed;
    if (item.key.toLowerCase() === 'passrate') return 'Pass Rate';
    if (item.key.toLowerCase() === 'frequency') return 'Executions';
    if (item.key.toLowerCase() === 'duration') return 'Avg. Duration';
    return '';
  };
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    let frameId = 0;
    const updatePlotBounds = (): void => {
      const svg = container.querySelector('svg');
      if (!(svg instanceof SVGSVGElement)) return;
      const yAxisLine = container.querySelector(
        '.MuiChartsAxis-directionY .MuiChartsAxis-line',
      ) as SVGGraphicsElement | null;
      const xAxisLine = container.querySelector(
        '.MuiChartsAxis-directionX .MuiChartsAxis-line',
      ) as SVGGraphicsElement | null;
      if (!xAxisLine) return;
      const svgRect = svg.getBoundingClientRect();
      const xRect = xAxisLine.getBoundingClientRect();
      const yRect = yAxisLine?.getBoundingClientRect();
      const rawLeft = yRect ? yRect.left - svgRect.left : xRect.left - svgRect.left;
      const rawTop = yRect ? yRect.top - svgRect.top : 16;
      const rawRight = xRect.right - svgRect.left;
      const rawBottom = xRect.top - svgRect.top;
      const svgWidth = svgRect.width;
      const svgHeight = svgRect.height;
      const left = Math.max(0, Math.min(rawLeft, svgWidth));
      const top = Math.max(0, Math.min(rawTop, svgHeight));
      const right = Math.max(left, Math.min(rawRight, svgWidth));
      const bottom = Math.max(top, Math.min(rawBottom, svgHeight));
      const width = Math.max(0, right - left);
      const plotHeight = Math.max(0, bottom - top);
      setPlotBounds({ left, top, width, height: plotHeight });
    };
    const scheduleUpdate = (): void => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(updatePlotBounds);
    };
    scheduleUpdate();
    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate();
    });
    resizeObserver.observe(container);
    const svg = container.querySelector('svg');
    if (svg instanceof SVGSVGElement) {
      resizeObserver.observe(svg);
    }
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [data, height, marginLeft, series, yAxisWidth]);
  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height,
        position: 'relative',
        overflow: 'visible',
        color: theme.app.colors.divider,
        '& .MuiChartsSurface-root': { color: theme.app.colors.divider },
        '& .MuiChartsLegend-root': { display: 'none' },
        '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
          stroke: theme.app.colors.divider,
          strokeOpacity: 1,
          strokeWidth: axisStrokeWidth,
        },
        '& .MuiChartsAxis-root .MuiChartsAxis-line, & .MuiChartsAxis-root .MuiChartsAxis-tick': {
          stroke: theme.app.colors.divider,
          strokeOpacity: 1,
          strokeWidth: axisStrokeWidth,
        },
      }}
    >
      {plotBounds ? (
        <Box
          sx={{
            position: 'absolute',
            left: plotBounds.left,
            top: plotBounds.top,
            width: plotBounds.width,
            height: plotBounds.height,
            backgroundColor: theme.palette.background.paper,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      ) : null}
      <MuiLineChart
        height={height}
        onLineClick={onSelectBar && !disableInteraction ? handleItemClick : undefined}
        onAxisClick={onSelectBar && !disableInteraction ? handleAxisClick : undefined}
        series={series.map((item, index) => ({
          data: data.map((row) => Number(row[item.key])),
          label: resolveSeriesLabel(item),
          color: item.color ?? palette[index % palette.length],
          showMark: showPoints,
          valueFormatter: (value) => formatTooltipValue(item.key, value),
        }))}
        xAxis={[
          {
            data: xLabels,
            scaleType: 'point',
            height: xAxisHeight,
            valueFormatter: (value) => formatChartDateLabel(String(value)),
            tickLabelInterval: hideXAxisLabels ? () => false : xTickLabelInterval,
            tickLabelStyle: {
              fill: hideXAxisLabels ? 'transparent' : theme.app.colors.textMuted,
              fontSize: hideXAxisLabels ? '0px' : '13px',
              transform: hideXAxisLabels ? 'none' : 'translateY(4px)',
            },
            tickSize: hideXAxisTicks ? 0 : 6,
          },
        ]}
        yAxis={[
          {
            min: yAxisMin,
            max: yAxisMax > 0 ? yAxisMax : undefined,
            width: yAxisWidth,
            tickLabelStyle: {
              fill: hideYAxisLabels ? 'transparent' : theme.app.colors.textMuted,
              fontSize: hideYAxisLabels ? '0px' : '13px',
            },
            tickSize: hideYAxisTicks ? 0 : 0,
            valueFormatter: formatYAxisValue,
          },
        ]}
        grid={{ horizontal: showHorizontalGrid, vertical: false }}
        margin={{ left: marginLeft, right: marginRight, top: marginTop, bottom: marginBottom }}
        slotProps={{ tooltip: { sx: getChartTooltipSx(theme), trigger: tooltipTrigger } }}
        sx={{
          position: 'relative',
          zIndex: 1,
          '& .MuiChartsAxis-tickLabel': { fill: theme.app.colors.textMuted },
          '& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel': {
            transform: `translateX(${yAxisTickTranslateX}px)`,
          },
          '& .MuiChartsAxis-root': { opacity: 1 },
          '& .MuiChartsGrid-line': {
            stroke: showHorizontalGrid ? theme.app.colors.divider : 'transparent',
          },
          '& .MuiChartsAxis-line[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
            strokeWidth: `${axisStrokeWidth}px !important`,
          },
          '& .MuiChartsAxis-tick[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
            strokeWidth: `${axisStrokeWidth}px !important`,
          },
          '& .MuiChartsAxis-directionX .MuiChartsAxis-line, & .MuiChartsAxis-directionX .MuiChartsAxis-line[stroke]':
            hideXAxisLine
              ? {
                  stroke: 'transparent !important',
                }
              : {
                  stroke: `${theme.app.colors.divider} !important`,
                  strokeWidth: `${axisStrokeWidth}px !important`,
                },
          '& .MuiChartsAxisHighlight-root, & .MuiChartsAxisHighlight': {
            display: 'none !important',
          },
          '& .MuiChartsAxisHighlight-line, & .MuiChartsAxisHighlightLine': {
            stroke: 'transparent !important',
            display: 'none',
          },
          '& .MuiChartsLegend-root': { display: 'none' },
          ...(onSelectBar && !disableInteraction
            ? {
                '& .MuiChartsAxis-directionX': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tickLabel': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tick': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-line, & .MuiChartsAxisHighlight-rect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsAxisHighlightLine, & .MuiChartsAxisHighlightRect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsOverlay-root': { cursor: 'pointer' },
                '& svg': { cursor: 'pointer' },
              }
            : null),
          ...(disableInteraction
            ? {
                '& .MuiChartsOverlay-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight': { pointerEvents: 'none' },
                '& .MuiLineElement-root': { pointerEvents: 'none' },
                '& .MuiMarkElement-root': { pointerEvents: 'none' },
              }
            : null),
        }}
      >
        {finiteReferenceLines.map((line, index) => (
          <ChartsReferenceLine
            key={`line-guide-${line.value}-${line.color}-${index}`}
            y={line.value}
            lineStyle={{ stroke: line.color, strokeDasharray: '8 6', strokeWidth: 0.5 }}
          />
        ))}
      </MuiLineChart>
      {!hideLegend ? (
        <Box sx={{ display: 'grid', gap: s('xs'), marginTop: s('sm') }}>
          {series.map((item, index) => (
            <Box key={item.key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: s('xs') }}>
                <Box
                  sx={{
                    width: s('sm'),
                    height: s('sm'),
                    backgroundColor: item.color ?? palette[index % palette.length],
                  }}
                />
                <Text role="body">{item.label}</Text>
              </Box>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};
